import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Providers from "./providers";
import RootLayout from "./layouts/root-layout";
import AppRoutesLayout from "./layouts/app-routes-layout";
import TravelJourneyStepsLayout from "./layouts/travel-journey-steps-layout";
import TameeniLoaderOverlay from "./components/ui/loader/tameeni-loader-overlay";
import LowercasePathRedirect from "./components/lowercase-path-redirect";
import ShortUrlRedirect from "./components/short-url-redirect";

const LoginPage = lazy(() => import("./pages/login"));
const InternalLoginPage = lazy(() => import("./pages/internal-login"));
const TravelInsurancePage = lazy(() => import("./pages/travel-insurance"));
const TravelerInfoPage = lazy(() => import("./pages/traveler-info"));
const QuoteListPage = lazy(() => import("./pages/quote-list"));
const PassportInfoPage = lazy(() => import("./pages/passport-info"));
const SummaryAndPaymentPage = lazy(() => import("./pages/summary-and-payment"));
const PaymentPage = lazy(() => import("./pages/payment"));
const SuccessPage = lazy(() => import("./pages/success"));
const UserAccountPage = lazy(() => import("./pages/user-account"));
const NotFoundPage = lazy(() => import("./pages/not-found"));
const ReceiptPage = lazy(() => import("./pages/receipt"));

function RootRedirect() {
  const { search } = useLocation();
  // Forward OIDC callback params (code, state, error) to the login route
  const isOidcCallback = search.includes("code=") || search.includes("error=");
  return (
    <Navigate
      to={isOidcCallback ? `/ar/login${search}` : "/ar"}
      replace
    />
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LowercasePathRedirect />
      <Suspense fallback={<TameeniLoaderOverlay transparent={false} />}>
        <Routes>
          {/* Short-url links (/SL/xxx) resolve on the backend — redirect there. */}
          <Route
            path="/sl/*"
            element={<ShortUrlRedirect />}
          />
          <Route
            path="/"
            element={<RootRedirect />}
          />
          <Route
            path="/:lng"
            element={
              <Providers>
                <RootLayout />
              </Providers>
            }
          >
            <Route
              path="login"
              element={<LoginPage />}
            />
            <Route
              path="receipt/:reference"
              element={<ReceiptPage />}
            />
            <Route element={<AppRoutesLayout />}>
              <Route
                path="user-account"
                element={<UserAccountPage />}
              />
              <Route
                path="payment"
                element={<PaymentPage />}
              />
              <Route
                path="success"
                element={<SuccessPage />}
              />
              <Route element={<TravelJourneyStepsLayout />}>
                <Route
                  index
                  element={<TravelInsurancePage />}
                />
                <Route
                  path="internal-login"
                  element={<InternalLoginPage />}
                />
                <Route
                  path="traveler-info"
                  element={<TravelerInfoPage />}
                />
                <Route
                  path="quote-list"
                  element={<QuoteListPage />}
                />
                <Route
                  path="passport-info"
                  element={<PassportInfoPage />}
                />
                <Route
                  path="summary-and-payment"
                  element={<SummaryAndPaymentPage />}
                />
              </Route>
            </Route>
            <Route
              path="*"
              element={<NotFoundPage />}
            />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
